import { useContext, useState } from "react";
import { Navigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "../../contexts/AuthContext";
import Logo from "../shared/Logo";

interface LoginFormData {
  email: string;
  password: string;
}

interface RecoveryFormData {
  email: string;
}

const loginValidationSchema = yup.object().shape({
  email: yup.string().required("Email richiesta").email("Email non valida"),
  password: yup.string().required("Password richiesta"),
});

const recoveryValidationSchema = yup.object().shape({
  email: yup.string().required("Email richiesta").email("Email non valida"),
});

export const Login = () => {
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, isLoggedIn } = useContext(AuthContext);

  const loginForm = useForm<LoginFormData>({
    resolver: yupResolver(loginValidationSchema),
  });

  const recoveryForm = useForm<RecoveryFormData>({
    resolver: yupResolver(recoveryValidationSchema),
  });

  if (isLoggedIn()) {
    return <Navigate to="/" />;
  }

  const handleLogin: SubmitHandler<LoginFormData> = (data) => {
    login(data.email, data.password)
      .then((res: any) => {
        if (res?.response?.status === 401) {
          toast.error("Credenziali non valide");
        }
      })
      .catch(() => {
        toast.error("Errore con la communicazione con il server");
      });
  };

  const handleRecovery: SubmitHandler<RecoveryFormData> = async (data) => {
    setIsLoading(true);
    try {
      const response = await axios.post("/auth/password-recovery", {
        email: data.email,
      });
      if (response?.data) {
        toast.success("Email di recupero inviata con successo");
        setIsRecoveryMode(false);
      } else {
        toast.error("Errore nell'invio dell'email di recupero");
      }
    } catch (error) {
      toast.error("Errore con la communicazione con il server");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          backgroundColor: "background.paper",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "3",
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            px: 3,
            py: "20px",
            width: "100%",
          }}
        >
          <Logo />

          {isRecoveryMode ? (
            <>
              <Typography variant="h5" align="center" sx={{ mb: 3 }}>
                Recupero Password
              </Typography>
              <form
                noValidate
                onSubmit={recoveryForm.handleSubmit(handleRecovery)}
              >
                <Stack spacing={3}>
                  <TextField
                    error={
                      !!(
                        recoveryForm.formState.touchedFields.email &&
                        recoveryForm.formState.errors.email
                      )
                    }
                    fullWidth
                    helperText={
                      recoveryForm.formState.touchedFields.email &&
                      recoveryForm.formState.errors.email?.message
                    }
                    label="Indirizzo Email"
                    disabled={isLoading}
                    {...recoveryForm.register("email")}
                  />
                </Stack>
                <Button
                  fullWidth
                  size="large"
                  sx={{ mt: 3 }}
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>INVIO IN CORSO...</span>
                    </Box>
                  ) : (
                    "INVIA EMAIL DI RECUPERO"
                  )}
                </Button>
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setIsRecoveryMode(false)}
                    disabled={isLoading}
                  >
                    Torna al login
                  </Link>
                </Box>
              </form>
            </>
          ) : (
            <>
              <form noValidate onSubmit={loginForm.handleSubmit(handleLogin)}>
                <Stack spacing={3}>
                  <TextField
                    error={
                      !!(
                        loginForm.formState.touchedFields.email &&
                        loginForm.formState.errors.email
                      )
                    }
                    fullWidth
                    helperText={
                      loginForm.formState.touchedFields.email &&
                      loginForm.formState.errors.email?.message
                    }
                    label="Indirizzo Email"
                    {...loginForm.register("email")}
                    autoComplete="username"
                  />
                  <TextField
                    error={
                      !!(
                        loginForm.formState.touchedFields.password &&
                        loginForm.formState.errors.password
                      )
                    }
                    fullWidth
                    helperText={
                      loginForm.formState.touchedFields.password &&
                      loginForm.formState.errors.password?.message
                    }
                    label="Password"
                    {...loginForm.register("password")}
                    type="password"
                    autoComplete="current-password"
                  />
                </Stack>
                <Box sx={{ mt: 1, textAlign: "right" }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setIsRecoveryMode(true)}
                  >
                    Hai dimenticato la password?
                  </Link>
                </Box>
                <Button
                  fullWidth
                  size="large"
                  sx={{ mt: 3 }}
                  type="submit"
                  variant="contained"
                >
                  LOGIN
                </Button>
              </form>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
